import { AsyncPipe, DatePipe } from '@angular/common'
import { Component, Input, OnDestroy, inject, ChangeDetectionStrategy } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatFormField } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { MatSort, MatSortHeader } from '@angular/material/sort'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import { Router } from '@angular/router'
import {
  DataContextBuilder,
  ElderDataToolbarComponent,
  ElderSearchBoxComponent,
  ElderSearchContextDirective,
  ElderSearchInputDirective,
  ElderSearchPanelComponent,
  ElderSinglePaneWrapperComponent,
  ElderTableComponent,
  ElderTableToolbarDirective,
  ElderToolbarContentDirective,
  Filter,
  IDataContext,
  Sort,
} from '@elderbyte/ngx-starter'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { ReplaySubject } from 'rxjs'
import { FilterBuilder } from '../../../orders/filter-builder'
import { WorkStation } from '../work-station'
import { WorkStationService } from '../work-station.service'

@Component({
  selector: 'app-work-station-browser',
  templateUrl: './work-station-browser.component.html',
  styleUrls: ['./work-station-browser.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ElderSinglePaneWrapperComponent,
    ElderTableComponent,
    MatSort,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    ElderToolbarContentDirective,
    ElderSearchBoxComponent,
    ElderSearchContextDirective,
    ElderSearchPanelComponent,
    MatFormField,
    MatInput,
    FormsModule,
    ElderSearchInputDirective,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatIcon,
    MatSortHeader,
    AsyncPipe,
    DatePipe,
    ElderTranslatePipe,
  ],
})
export class WorkStationBrowserComponent implements OnDestroy {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  public readonly data: IDataContext<WorkStation>
  public readonly forcedFilters$ = new ReplaySubject<Filter[]>(1)
  private router = inject(Router)
  private workStationService = inject(WorkStationService)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    this.data = DataContextBuilder.start<WorkStation>()
      .pageSize(50)
      .reloadOnLocalChanges()
      .autoStartOnFiltersAll([FilterBuilder.FACILITY_KEY], [new Sort('number', 'asc')])
      .build(this.workStationService)
  }

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnDestroy(): void {
    if (this.data) {
      this.data.close()
    }
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input({ required: true })
  public set facility(facilityId: string) {
    if (facilityId) {
      const facilityFilter = FilterBuilder.facility(facilityId)
      this.forcedFilters$.next([facilityFilter])
    }
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public openSideDetail(workStation: WorkStation): void {
    this.router.navigate([{ outlets: { side: ['shop-floor', 'work-stations', workStation.id] } }])
  }

  public openDetail(workStation: WorkStation): void {
    if (workStation) {
      this.router.navigate(['app', 'shop-floor', 'work-stations', workStation.id])
    }
  }

  public create(): void {
    this.router.navigate([{ outlets: { side: ['shop-floor', 'work-stations', 'new'] } }])
  }
}
